// Planilha Gran — acesso ao banco. Gravação só pelas RPCs (ver
// supabase/migrations/20260925120000_planilha_gran.sql): as tabelas não têm policy de
// escrita, e o autor de cada mudança é preenchido no servidor.
import { supabase } from '../../../src/lib/supabase';
import type { ItemPlanilhaGran, LinhaEnvioGran } from './planilhaGranModel';
import type { PlanoImportacao } from './lerPlanilhaConferencia';

export interface EnvioPlanilhaGran {
  id: number;
  mes_referencia: string;
  qtd_linhas: number;
  total_boleto: number;
  total_equatorial: number;
  total_comissao: number;
  filtro: string | null;
  gerado_por: string | null;
  gerado_em: string;
  linhas?: LinhaEnvioGran[];
}

/** Erro do PostgREST/RPC com a mensagem que o banco levantou (RAISE EXCEPTION). */
function erroLegivel(error: any, padrao: string): Error {
  const msg = String(error?.message || '');
  if (error?.code === '42501') return new Error('Seu perfil não pode alterar a planilha Gran.');
  if (error?.code === '42P01' || error?.code === 'PGRST205' || /does not exist|schema cache/i.test(msg)) {
    return new Error('A planilha Gran ainda não foi instalada no banco (falta aplicar a migration).');
  }
  return new Error(msg || padrao);
}

export async function listarItens(empresaId: string): Promise<ItemPlanilhaGran[]> {
  const { data, error } = await supabase
    .from('planilha_gran_itens')
    .select('id, unidade_consumidora_id, numero_uc, grupo, origem, anotacao, conferencia')
    .eq('empresa_parceira_id', empresaId)
    .eq('ativo', true)
    .order('grupo');
  if (error) throw erroLegivel(error, 'Não foi possível carregar a lista da planilha Gran.');
  return (data || []) as ItemPlanilhaGran[];
}

export async function incluirUcs(empresaId: string, ucIds: number[], grupo: string | null): Promise<number> {
  const { data, error } = await supabase.rpc('planilha_gran_incluir_ucs', {
    p_empresa: empresaId, p_uc_ids: ucIds, p_grupo: grupo, p_origem: 'extra',
  });
  if (error) throw erroLegivel(error, 'Não foi possível incluir as UCs.');
  return Number(data) || 0;
}

export async function removerItens(itemIds: number[]): Promise<number> {
  const { data, error } = await supabase.rpc('planilha_gran_remover_itens', { p_item_ids: itemIds });
  if (error) throw erroLegivel(error, 'Não foi possível tirar as UCs da lista.');
  return Number(data) || 0;
}

export async function moverGrupo(itemIds: number[], grupo: string): Promise<number> {
  const { data, error } = await supabase.rpc('planilha_gran_mover_grupo', { p_item_ids: itemIds, p_grupo: grupo });
  if (error) throw erroLegivel(error, 'Não foi possível mudar o condomínio.');
  return Number(data) || 0;
}

/** Grava a leitura da planilha de conferência da Gran (#6116) numa transação só. */
export async function importarConferencia(
  empresaId: string,
  plano: PlanoImportacao,
  removerIds: number[],
): Promise<{ atualizadas: number; incluidas: number; removidas: number }> {
  const { data, error } = await supabase.rpc('planilha_gran_importar_conferencia', {
    p_empresa: empresaId,
    p_atualizar: plano.atualizar.map(a => ({ item_id: a.itemId, anotacao: a.anotacao, conferencia: a.conferencia })),
    p_incluir: plano.incluir.map(n => ({ uc_id: n.ucId, anotacao: n.anotacao, conferencia: n.conferencia })),
    p_remover: removerIds,
  });
  if (error) {
    if (error.code === 'PGRST202' || /could not find the function/i.test(error.message || '')) {
      throw new Error('A leitura da planilha ainda não foi instalada no banco (falta aplicar a migration).');
    }
    throw erroLegivel(error, 'Não foi possível gravar a planilha da Gran.');
  }
  const r = (data || {}) as any;
  return { atualizadas: Number(r.atualizadas) || 0, incluidas: Number(r.incluidas) || 0, removidas: Number(r.removidas) || 0 };
}

export async function registrarEnvio(empresaId: string, mes: string, linhas: LinhaEnvioGran[], filtro: string | null): Promise<number> {
  const { data, error } = await supabase.rpc('planilha_gran_registrar_envio', {
    p_empresa: empresaId, p_mes: mes, p_linhas: linhas, p_filtro: filtro,
  });
  if (error) throw erroLegivel(error, 'A planilha foi baixada, mas não ficou registrada no histórico.');
  return Number(data);
}

export async function listarEnvios(empresaId: string): Promise<EnvioPlanilhaGran[]> {
  const { data, error } = await supabase
    .from('planilha_gran_envios')
    .select('id, mes_referencia, qtd_linhas, total_boleto, total_equatorial, total_comissao, filtro, gerado_por, gerado_em')
    .eq('empresa_parceira_id', empresaId)
    .order('gerado_em', { ascending: false })
    .limit(200);
  if (error) throw erroLegivel(error, 'Não foi possível carregar o histórico de planilhas.');
  return (data || []) as EnvioPlanilhaGran[];
}

/** As linhas ficam fora da listagem (podem ser centenas por envio): só vêm ao reabrir. */
export async function carregarLinhasDoEnvio(id: number): Promise<LinhaEnvioGran[]> {
  const { data, error } = await supabase.from('planilha_gran_envios').select('linhas').eq('id', id).single();
  if (error) throw erroLegivel(error, 'Não foi possível abrir esta planilha.');
  return ((data as any)?.linhas || []) as LinhaEnvioGran[];
}

/** Nome de quem gerou cada envio — `profiles.full_name` (a tabela não tem `nome`). */
export async function nomesDosAutores(ids: string[]): Promise<Record<string, string>> {
  const unicos = Array.from(new Set(ids.filter(Boolean)));
  if (!unicos.length) return {};
  const { data } = await supabase.from('profiles').select('id, full_name').in('id', unicos);
  return Object.fromEntries((data || []).map((p: any) => [p.id, p.full_name || '']));
}
